import type {
  AppConfig,
  SessionInfo,
  AssessmentPayload,
  CurveSpec,
  DecisionResult,
  MIPrompts,
  PatientRow,
} from "./types";

// ---------------------------------------------------------------- session token
// Bearer token kept in sessionStorage (per tab, cleared when the tab closes). Works inside
// EHR iframes where third-party cookies are blocked.
const TOKEN_KEY = "pedob_token";
let token: string | null = (() => {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
})();

export function setToken(t: string | null) {
  token = t;
  try {
    if (t) sessionStorage.setItem(TOKEN_KEY, t);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage blocked: keep in memory only */
  }
}

export const hasToken = () => !!token;

/** fetch that reports a dropped connection as a readable message (aborts pass through). */
async function send(url: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new Error("Can't reach the server. Check your internet connection and try again.");
  }
}

/** An HTTP failure as a sentence a user can act on: no status codes, no raw JSON. */
async function errorMessage(res: Response): Promise<string> {
  if (res.status >= 500) return "Something went wrong on the server. Please try again in a moment.";
  let detail: unknown = null;
  try {
    detail = (await res.json()).detail;
  } catch {
    /* non-JSON error body */
  }
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail)) {
    // FastAPI validation errors: [{loc: [..., "field"], msg}]
    const parts = detail.map((d: { loc?: unknown[]; msg?: string }) => {
      const field = Array.isArray(d?.loc) ? String(d.loc[d.loc.length - 1]).replace(/_/g, " ") : "value";
      return `${field}: ${d?.msg ?? "invalid"}`;
    });
    return `Please check the entered values (${parts.join("; ")}).`;
  }
  if (res.status === 403) return "You don't have access to this.";
  if (res.status === 404) return "Not found.";
  return "The request could not be completed. Please try again.";
}

/** fetch with the session token; a 401 ends the session everywhere in the app. */
async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const res = await send(url, { ...init, headers });
  if (res.status === 401 && token) {
    setToken(null);
    window.dispatchEvent(new CustomEvent("pedob:signed-out", { detail: "Your session ended. Please sign in again." }));
  }
  return res;
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(await errorMessage(res));
  return res.json() as Promise<T>;
}

const get = (url: string) => authFetch(url);
const post = (url: string, body?: unknown) =>
  authFetch(url, {
    method: "POST",
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

type LoginResult = { token: string; session: SessionInfo };

export const api = {
  config: () => send("/api/config").then((r) => json<AppConfig>(r)),
  login: (username: string, password: string) =>
    send("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    }).then((r) => json<LoginResult>(r)),
  smartLogin: (body: { fhir_server: string; access_token: string; patient_id: string; fhir_user: string | null }) =>
    send("/api/auth/smart", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => json<LoginResult>(r)),
  me: () => get("/api/auth/me").then((r) => json<SessionInfo>(r)),
  logout: () => post("/api/auth/logout").finally(() => setToken(null)),
  patients: () => get("/api/patients").then((r) => json<PatientRow[]>(r)),
  assessment: (id: string) =>
    get(`/api/patients/${encodeURIComponent(id)}/assessment`).then((r) => json<AssessmentPayload>(r)),
  curves: (sex: string) => get(`/api/reference/bmi-curves?sex=${sex}`).then((r) => json<CurveSpec>(r)),
  assessFhir: (bundle: unknown, fhirServer: string) =>
    post("/api/assess/fhir", { bundle, fhir_server: fhirServer }).then((r) => json<AssessmentPayload>(r)),
  assessManual: (body: unknown) => post("/api/assess/manual", body).then((r) => json<AssessmentPayload>(r)),
  decisions: (id: string, body: unknown) =>
    post(`/api/patients/${encodeURIComponent(id)}/decisions`, body).then((r) => json<DecisionResult>(r)),
  writebackResult: (id: string, ok: boolean, detail: string) =>
    post(`/api/patients/${encodeURIComponent(id)}/writeback-result`, { ok, detail }),
  demoReset: () =>
    post("/api/admin/demo/reset").then((r) =>
      json<{ snapshots_removed: number; decisions_removed: number; patients_seeded: number }>(r),
    ),
  accessLog: () => get("/api/admin/access-log").then((r) => json<Record<string, string | null>[]>(r)),
  audit: () =>
    get("/api/audit").then((r) =>
      json<{ decisions: Record<string, string>[]; override_rates: Record<string, string | number | null>[] }>(r),
    ),
  mi: (id: string) => post(`/api/patients/${encodeURIComponent(id)}/ai/mi`).then((r) => json<MIPrompts>(r)),
  aiContext: (id: string) => get(`/api/patients/${encodeURIComponent(id)}/ai/context`).then((r) => json<unknown>(r)),
  fhirPatients: () =>
    get("/api/fhir/patients").then((r) =>
      json<{ id: string; name: string | null; birth_date: string; gender: string }[]>(r),
    ),
  fhirImport: (id: string) => post(`/api/fhir/import/${encodeURIComponent(id)}`).then((r) => json<AssessmentPayload>(r)),
  hl7Inbound: (text: string) =>
    authFetch("/api/hl7/inbound", { method: "POST", body: text }).then(async (r) => ({
      ack: await r.text(),
      patientId: r.headers.get("X-PedOb-Patient") || null,
    })),
  cdsDiscovery: () => send("/cds-services").then((r) => json<unknown>(r)),
};

/** Stream an AI endpoint's plain-text response, calling onText with the accumulated text. */
export async function streamAi(
  url: string,
  body: unknown,
  onText: (text: string) => void,
  signal?: AbortSignal,
): Promise<{ aiGenerated: boolean }> {
  const res = await authFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
    signal,
  });
  if (!res.ok) throw new Error(await errorMessage(res));
  if (!res.body) throw new Error("The response could not be read. Please try again.");
  const aiGenerated = res.headers.get("X-AI-Generated") === "true";
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let acc = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    acc += decoder.decode(value, { stream: true });
    onText(acc);
  }
  return { aiGenerated };
}
